var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { CommandSocketInitializer } from './framework/CommandSocketInitializer';
import { Timeouts } from './framework/Timeouts';
import { TransportError } from './framework/Transport';
export class Denon900R {
    constructor(socket, state, log, framework) {
        this.socket = socket;
        this.state = state;
        this.log = log;
        this.framework = framework;
        // other class properties and constructor
        this.configuredCodes = [];
        this.currentTrack = '0001';
        this.ignorePollDurationMs = 2000;
        this.afterStopDurationMs = 1000;
        this.lastUpdateTime = 0;
        this.oneTouchRecording = false;
        this.playerMediaOperationCaps = [
            'mediaPlayerForward',
            'mediaPlayerNext',
            'mediaPlayerPause',
            'mediaPlayerPlay',
            'mediaPlayerPrevious',
            'mediaPlayerReverse',
            'mediaPlayerStop',
        ];
        this.recorderMediaOperationCaps = [
            'mediaRecorderRecord',
            'mediaRecorderPause',
            'mediaRecorderStop',
        ];
        this.mediaStateMap = {
            AB: 'playing',
            ED: 'stopped',
            FF: 'forwarding',
            PL: 'playing',
            PP: 'paused',
            PR: 'paused',
            RE: 'recording',
            RW: 'reversing',
            // The CE state is not defined in the Denon API guide. However, we see this response when
            // playback of a file has reached its end.
            CE: 'stopped',
            ST: 'stopped',
        };
        this.mediaRecorderStateMap = {
            ED: 'stopped',
            WR: 'stopped',
            RE: 'recording',
            RP: 'paused',
            ST: 'stopped',
        };
        this.timeouts = new Timeouts();
        // controls
        this.busy = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                return this.state.busy.value;
            }),
        };
        this.firmwareVersion = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                return this.state.firmwareVersion.value;
            }),
            hasControl: () => __awaiter(this, void 0, void 0, function* () {
                try {
                    const match = yield this.socket.send(`@0?VN`, /@\dVN(.+)/i, [
                        'get.firmwareVersion',
                    ]);
                    if (match[1] !== undefined) {
                        this.state.firmwareVersion.update(match[1]);
                        return true;
                    }
                    return false;
                }
                catch (error) {
                    if (error instanceof TransportError && error.code === 'sendTimeout')
                        return false;
                    throw error;
                }
            }),
            powerOffRestrictions: ['get', 'hasControl'],
        };
        this.mediaPlayerState = {
            get: (key, tags) => __awaiter(this, void 0, void 0, function* () {
                const state = this.state.mediaPlayerState.values[key];
                if (state === undefined) {
                    this.log.error(`no mediaPlayerState for ${key}`);
                    return 'stopped';
                }
                return state;
            }),
            getKeys: () => this.configuredCodes.filter(code => this.configuredCodes.includes(code)),
        };
        this.mediaRecorderState = {
            get: (key, tags) => __awaiter(this, void 0, void 0, function* () {
                var _a;
                // TODO: We need a way to query the recorder state. For example, if the driver connects
                // while the software is in the middle of recording, how will we know it is recording?
                return (_a = this.state.mediaRecorderState.values[key]) !== null && _a !== void 0 ? _a : 'stopped';
            }),
        };
        this.mediaOperation = {
            set: (key, value, tags) => __awaiter(this, void 0, void 0, function* () {
                var _b, _c;
                switch (value.operation) {
                    case 'mediaPlayerForward': {
                        const match = yield this.socket.send(`@02352`, /(\x06|\x15)/i, tags);
                        this.lastUpdateTime = Date.now();
                        if (match[1].charCodeAt(0) === 6) {
                            this.state.mediaPlayerState.update(key, this.mediaStateMap['FF']);
                        }
                        break;
                    }
                    case 'mediaPlayerPlay': {
                        const match = yield this.socket.send(`@02353`, /^(\x06|\x15)/i, tags);
                        if (match[1] && match[1].charCodeAt(0) === 6) {
                            this.lastUpdateTime = Date.now();
                            this.state.mediaPlayerState.update(key, this.mediaStateMap['PL']);
                        }
                        break;
                    }
                    case 'mediaPlayerReverse': {
                        const match = yield this.socket.send(`@02350`, /(\x06|\x15)/i, tags);
                        this.lastUpdateTime = Date.now();
                        if (match[1].charCodeAt(0) === 6) {
                            this.state.mediaPlayerState.update(key, this.mediaStateMap['RW']);
                        }
                        break;
                    }
                    case 'mediaPlayerStop': {
                        const match = yield this.socket.send(`@02354`, /^(\x06|\x15)/i, tags);
                        if (match[1] && match[1].charCodeAt(0) === 6) {
                            this.lastUpdateTime = Date.now();
                            this.state.mediaPlayerState.update(key, 'stopped');
                            this.state.mediaRecorderState.update(key, 'stopped');
                        }
                        break;
                    }
                    case 'mediaPlayerPrevious': {
                        const match = yield this.socket.send(`@02333`, /^(\x06|\x15)/, tags);
                        if (match[1] && match[1].charCodeAt(0) === 6) {
                            this.lastUpdateTime = Date.now();
                        }
                        break;
                    }
                    case 'mediaPlayerNext': {
                        const match = yield this.socket.send(`@02332`, /^(\x06|\x15)/, tags);
                        if (match[1] && match[1].charCodeAt(0) === 6) {
                            this.lastUpdateTime = Date.now();
                        }
                        break;
                    }
                    case 'mediaPlayerPause': {
                        // pause or pausing a pause will cause the system to play
                        const match = yield this.socket.send(`@02348`, /(\x06|\x15)/i, tags);
                        if (match[1] && match[1].charCodeAt(0) === 6) {
                            this.lastUpdateTime = Date.now();
                            this.state.mediaPlayerState.update(key, this.mediaStateMap['PP']);
                        }
                        break;
                    }
                    case 'mediaRecorderRecord':
                        // If already recording, then don't start recording again.
                        try {
                            // The response may indicate recording or recording paused, or no response
                            // if already recording. Ignore the \x06 success response, we're looking for
                            // the STRE/STRP status.
                            let match = yield this.socket.send(`@02355`, /(@0STR[EP]|\x15)/i, tags);
                            // If not in one touch recording mode or we received a recording pause
                            // status send a second record start command.
                            if (!this.oneTouchRecording && match[1] !== '@0STRE') {
                                match = yield this.socket.send(`@02355`, /(@0STRE|\x15)/i, tags);
                                this.lastUpdateTime = Date.now();
                            }
                            if (match[1].toUpperCase() === '@0STRE') {
                                this.state.mediaRecorderState.update(key, 'recording');
                                if (this.state.mediaRecorderState.values[(_b = this.currentInputCode) !== null && _b !== void 0 ? _b : '1'] === 'recording') {
                                    // Get the recording time from the device in case recording had been
                                    // paused and now is continuing.
                                    const timeMatch = yield this.socket.send(`@0?ET`, /@0?ET(\d{3})(\d{2})(\d{2})/i, tags);
                                    const hh = parseInt(timeMatch[1], 10);
                                    const mm = parseInt(timeMatch[2], 10);
                                    const ss = parseInt(timeMatch[3], 10);
                                    const elapsedTime = ((hh * 60 + mm) * 60 + ss) * 1000;
                                    this.state.mediaRecorderStartTime.update((_c = this.currentInputCode) !== null && _c !== void 0 ? _c : '1', Date.now() - elapsedTime);
                                }
                            }
                        }
                        catch (error) {
                            if (error instanceof TransportError && error.code === 'sendTimeout') {
                                this.log.info(`timeout waiting for record command`);
                            }
                        }
                        break;
                    case 'mediaRecorderPause': {
                        const match = yield this.socket.send(`@023Rp`, /(@0STRp|\x15)/i, tags);
                        if (match[1].toUpperCase() === '@0STRP') {
                            this.lastUpdateTime = Date.now();
                            this.state.mediaRecorderState.update(key, 'paused');
                            this.state.mediaPlayerState.update(key, 'stopped');
                        }
                        break;
                    }
                    case 'mediaRecorderStop': {
                        const match = yield this.socket.send(`@02354`, /^(\x06|\x15)/i, tags);
                        if (match[1] &&
                            (match[1].charCodeAt(0) === 6 || match[1].toUpperCase() === '@0STST')) {
                            this.lastUpdateTime = Date.now();
                            this.state.mediaPlayerState.update(key, 'stopped');
                            this.state.mediaRecorderState.update(key, 'stopped');
                        }
                        break;
                    }
                    default:
                        break;
                }
                return value;
            }),
            asyncHandlers: [
                {
                    regExp: /^@0ST(PP|RE|PL|RP|ED)/i,
                    callback: (match) => {
                        this.updateState(match[1]);
                    },
                },
                {
                    regExp: /^@0Tr(\d+)/i,
                    callback: (match) => {
                        // TODO: add support for bubbling up track to the Inara UI
                        this.currentTrack = match[1];
                        this.log.info(`current track: ${this.currentTrack}`);
                    },
                },
            ],
        };
        this.mediaOperations = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                const ops = {};
                for (const code of this.configuredCodes) {
                    ops[code] = [...this.playerMediaOperationCaps, ...this.recorderMediaOperationCaps];
                }
                return ops;
            }),
        };
        this.mediaRecorderStartTime = {
            get: (key, tags) => __awaiter(this, void 0, void 0, function* () {
                var _d, _e, _f;
                const match = yield this.socket.send(`@0?ET`, /@0?ET(\d{3})(\d{2})(\d{2})/i, tags);
                const hh = parseInt(match[1], 10);
                const mm = parseInt(match[2], 10);
                const ss = parseInt(match[3], 10);
                const elapsedTime = ((hh * 60 + mm) * 60 + ss) * 1000;
                if (this.state.mediaRecorderState.values[(_d = this.currentInputCode) !== null && _d !== void 0 ? _d : '1'] === 'recording') {
                    this.state.mediaRecorderStartTime.update((_e = this.currentInputCode) !== null && _e !== void 0 ? _e : '1', Date.now() - elapsedTime);
                }
                return elapsedTime > 0 &&
                    this.state.mediaRecorderState.values[(_f = this.currentInputCode) !== null && _f !== void 0 ? _f : '1'] === 'recording'
                    ? Date.now() - elapsedTime
                    : 0;
            }),
        };
        this.power = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                const match = yield this.socket.send(`@0?PW`, /@0PW0(\d)/i, tags);
                return match[1] === '0';
            }),
            set: (power, tags) => __awaiter(this, void 0, void 0, function* () {
                this.state.busy.update(true);
                this.framework.suspendPolling(15000);
                // The Denon drops the network connection after responding to the power on command.
                const match = yield this.socket.send(`${power ? '@023PW' : '@02312'}`, /^(\x06|\x15)/i, tags);
                // Successful power command for the device will return an \x06.
                if (match[1].charCodeAt(0) === 0x06) {
                    if (power) {
                        // Then the network will drop after 3 to 5 seconds.  We query the power for up
                        // to 25 seconds until we can reconnect.
                        yield this.timeouts.delay(5000);
                        const startTime = Date.now();
                        let newPower;
                        while (newPower === undefined && startTime + 25000 > Date.now()) {
                            try {
                                const match = yield this.socket.send(`@0?PW`, /@0PW0(\d)/, [
                                    'get.power',
                                ]);
                                if (match[1]) {
                                    newPower = match[1] === '0';
                                    this.state.busy.update(false);
                                    return newPower;
                                }
                                yield this.timeouts.delay(2000);
                            }
                            catch (error) {
                                // Ignore errors and keep trying.
                                this.timeouts.clearAll();
                                yield this.framework.reconnect({
                                    initialDelayMs: 500,
                                    attemptDelayMs: 5000,
                                    numAttempts: 3,
                                });
                                yield this.timeouts.delay(2000);
                            }
                        }
                        this.state.busy.update(false);
                        return this.state.power.value;
                    }
                    else {
                        // Power was turned off successfully.
                        this.state.busy.update(false);
                        return power;
                    }
                }
                this.state.busy.update(false);
                return this.state.power.value;
            }),
        };
        this.videoInput = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                return '1';
            }),
            set: (code, tags) => __awaiter(this, void 0, void 0, function* () {
                return code;
            }),
            powerOffRestrictions: ['get', 'set'],
        };
        // polling
        this.polls = [
            {
                intervalMs: 4000,
                function: () => __awaiter(this, void 0, void 0, function* () {
                    const match = yield this.socket.send(`@0?ST`, /@0ST(.+)/i, ['poll']);
                    if (Date.now() < this.lastUpdateTime + this.ignorePollDurationMs)
                        return;
                    this.updateState(match[1]);
                }),
            },
        ];
    }
    // phase callbacks
    onConfigure(config) {
        var _a, _b, _c;
        return __awaiter(this, void 0, void 0, function* () {
            this.timeouts.clearAll();
            this.configuredCodes = Object.values((_a = config.videoInputs) !== null && _a !== void 0 ? _a : {}).map(v => v.code);
            this.configuredCodes.forEach(code => this.state.mediaPlayerState.update(code, 'stopped'));
            this.configuredCodes.forEach(code => this.state.mediaRecorderState.update(code, 'stopped'));
            if (this.configuredCodes.length > 0 &&
                (this.currentInputCode === undefined ||
                    !this.configuredCodes.includes(this.currentInputCode))) {
                this.currentInputCode = this.configuredCodes.sort()[0];
            }
            // Determine if one-touch recording is on.
            let match = yield this.socket.send(`@0?OR`, /@0OR0(\d)/i, ['configure']);
            this.oneTouchRecording = match[1] === '0';
            // Get the initial state.
            match = yield this.socket.send(`@0?ST`, /@0ST(.+)/i, ['configure']);
            this.updateState(match[1]);
            this.state.mediaRecorderState.update((_b = this.currentInputCode) !== null && _b !== void 0 ? _b : '1', (_c = this.mediaRecorderStateMap[match[1]]) !== null && _c !== void 0 ? _c : 'stopped');
        });
    }
    onDisconnect() {
        return __awaiter(this, void 0, void 0, function* () {
            this.timeouts.clearAll();
        });
    }
    onInitialize(credentials, config) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            const initializerSteps = [
                {
                    errorCode: 'verifyFailed',
                    send: '@0?PW',
                    succeed: /@0PW(\d+)/i,
                    next: /Unauthorized/i,
                },
                // Note, I have never been able to log in successfully when a password is required. I
                // suspect there's a bug in the Denon firmware.
                {
                    errorCode: 'authFailed',
                    send: `@0?LI${(_a = credentials.password) !== null && _a !== void 0 ? _a : ''}`,
                    succeed: /@0LIOK/i,
                    fail: /@0LING/i,
                },
            ];
            return yield CommandSocketInitializer.initialize(this.socket, initializerSteps, credentials);
        });
    }
    // other class methods
    updateState(resp) {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j;
        if (Date.now() < this.lastUpdateTime + this.afterStopDurationMs)
            return;
        if (['RP', 'RE', 'ST', 'CE'].includes(resp)) {
            // Stop stops any recording
            this.state.mediaRecorderState.update((_a = this.currentInputCode) !== null && _a !== void 0 ? _a : '1', (_b = this.mediaRecorderStateMap[resp]) !== null && _b !== void 0 ? _b : 'stopped');
            if (resp === 'ST' || resp === 'CE') {
                // Stop stops any playback
                this.state.mediaPlayerState.update((_c = this.currentInputCode) !== null && _c !== void 0 ? _c : '1', (_d = this.mediaStateMap[resp]) !== null && _d !== void 0 ? _d : 'stopped');
            }
            if (resp === 'RE') {
                this.mediaRecorderStartTime.get((_e = this.currentInputCode) !== null && _e !== void 0 ? _e : '1', [
                    'get.recordingTime',
                ]);
            }
        }
        else {
            this.state.mediaPlayerState.update((_f = this.currentInputCode) !== null && _f !== void 0 ? _f : '1', (_g = this.mediaStateMap[resp]) !== null && _g !== void 0 ? _g : 'stopped');
            this.state.mediaRecorderState.update((_h = this.currentInputCode) !== null && _h !== void 0 ? _h : '1', (_j = this.mediaRecorderStateMap[resp]) !== null && _j !== void 0 ? _j : 'stopped');
        }
    }
}
// static properties
Denon900R.driverType = 'socketDriver';
Denon900R.key = '-Ng0-GSYbUtU_bxdP2kO';
Denon900R.baseSocketOptions = {
    commandExtractor: {
        terminators: [''],
        // can return \x06, \x15, or \x0d terminated command
        unterminatedRegExp: /^((?:\x06|\x15)?[^\x0d]*)(?:\x0d)?/i,
    },
    idleTimeoutMs: 30000,
    sendTerminator: '\r',
};
Denon900R.socketOptions = [
    Object.assign(Object.assign({}, Denon900R.baseSocketOptions), { port: 23 }),
    Object.assign(Object.assign({}, Denon900R.baseSocketOptions), { port: 4999 }),
];
